import type { ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Layout } from './components/Layout';
import { useAuth, type Role } from './lib/auth';
import { AdminPage } from './pages/AdminPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { AuthPage, MagicLinkPage, PrivacyPromise } from './pages/AuthPage';
import { BillingPage } from './pages/BillingPage';
import { CallsPage } from './pages/CallsPage';
import { ConsentPage } from './pages/ConsentPage';
import { DashboardPage } from './pages/DashboardPage';
import { ArchivePage } from './pages/ArchivePage';
import { ElderModePage } from './pages/ElderModePage';
import { LanguagesPage } from './pages/LanguagesPage';
import { TimelinePage } from './pages/TimelinePage';
import { LegacyBookPage } from './pages/LegacyBookPage';
import { LiveCirclesPage } from './pages/LiveCirclesPage';
import { PlayPage } from './pages/PlayPage';
import { ReportPage, SafetyPage } from './pages/SafetyPage';
import { StoryVaultPage } from './pages/StoryVaultPage';
import { TeenRootsPage } from './pages/TeenRootsPage';
import { TripsPage } from './pages/TripsPage';
import { TutorConsolePage } from './pages/TutorConsolePage';
import { VoiceTreePage } from './pages/VoiceTreePage';

function homeFor(role: Role): string {
  if (role === 'tutor') return '/tutor';
  if (role === 'safeguarding_admin' || role === 'content_editor') return '/admin';
  if (role === 'child') return '/play';
  return '/';
}

/** Client-side gate for navigation only — every rule is enforced again by the API. */
function Guard({ roles, children }: { roles: Role[]; children: ReactNode }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (!user) return <Navigate to="/signin" state={{ from: loc.pathname }} replace />;
  if (!roles.includes(user.role)) return <Navigate to={homeFor(user.role)} replace />;
  return <>{children}</>;
}

const FAMILY: Role[] = ['parent', 'grandparent', 'teen'];

export function App() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/signin" element={user ? <Navigate to={homeFor(user.role)} /> : <AuthPage />} />
      <Route path="/signup" element={user ? <Navigate to={homeFor(user.role)} /> : <AuthPage initial="signup" />} />
      <Route path="/auth/magic" element={<MagicLinkPage />} />
      <Route path="/privacy-promise" element={<PrivacyPromise />} />
      <Route element={<Guard roles={['parent', 'child', 'teen', 'grandparent', 'tutor', 'safeguarding_admin', 'content_editor']}><Layout /></Guard>}>
        <Route index element={<Guard roles={FAMILY}><DashboardPage /></Guard>} />
        <Route path="consent" element={<Guard roles={['parent']}><ConsentPage /></Guard>} />
        <Route path="tree" element={<Guard roles={[...FAMILY, 'child']}><VoiceTreePage /></Guard>} />
        <Route path="stories" element={<Guard roles={[...FAMILY, 'child']}><StoryVaultPage /></Guard>} />
        <Route path="preserve" element={<Guard roles={FAMILY}><ElderModePage /></Guard>} />
        <Route path="archive" element={<Guard roles={[...FAMILY, 'child']}><ArchivePage /></Guard>} />
        <Route path="elder" element={<Guard roles={['parent', 'grandparent']}><ElderModePage elder /></Guard>} />
        <Route path="timeline" element={<Guard roles={FAMILY}><TimelinePage /></Guard>} />
        <Route path="languages" element={<Guard roles={FAMILY}><LanguagesPage /></Guard>} />
        <Route path="play" element={<Guard roles={['parent', 'child', 'teen']}><PlayPage /></Guard>} />
        <Route path="roots" element={<Guard roles={['parent', 'teen']}><TeenRootsPage /></Guard>} />
        <Route path="live" element={<Guard roles={['parent', 'teen']}><LiveCirclesPage /></Guard>} />
        <Route path="calls" element={<Guard roles={FAMILY}><CallsPage /></Guard>} />
        <Route path="trips" element={<Guard roles={['parent']}><TripsPage /></Guard>} />
        <Route path="legacy-book" element={<Guard roles={['parent']}><LegacyBookPage /></Guard>} />
        <Route path="safety" element={<Guard roles={['parent']}><SafetyPage /></Guard>} />
        <Route path="report" element={<ReportPage />} />
        <Route path="billing" element={<Guard roles={['parent']}><BillingPage /></Guard>} />
        <Route path="tutor" element={<Guard roles={['tutor']}><TutorConsolePage /></Guard>} />
        <Route path="admin" element={<Guard roles={['safeguarding_admin', 'content_editor']}><AdminPage /></Guard>} />
        {/* Signed in: an address that does not exist says so, inside the app, with
            the navigation still there. It used to redirect home in silence, which
            told a person nothing about the link they had just followed. */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
      {/* Signed out, everything unknown still goes to sign-in: there is nothing to
          show someone who is not in, and the address may have been a deep link they
          will reach once they are. */}
      <Route path="*" element={<Navigate to="/signin" replace />} />
    </Routes>
  );
}
